import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { App, Avatar, Button, Card, Checkbox, DatePicker, Empty, Form, Image, Input, InputNumber, List, Modal, Rate, Select, Space, Spin, Table, Tag, Typography, Upload } from 'antd'
import { DeleteOutlined, EditOutlined, EyeOutlined, PlusOutlined, SearchOutlined, UploadOutlined } from '@ant-design/icons'
import type { ColumnsType } from 'antd/es/table'
import type { UploadFile } from 'antd/es/upload/interface'
import dayjs, { type Dayjs } from 'dayjs'
import { sellerProductsAPI, type SellerProductReview } from '../utils/sellerApi'
import { convertFromInrToSellerCurrency, convertSellerCurrencyToInr, formatSellerCurrency, useSellerCurrencyStore } from '../utils/currency'
import { useSellerAuth } from '../contexts/SellerAuthContext'

interface ProductImageItem {
  id: number
  url: string
  is_primary: boolean
}

interface ProductCategory {
  id: number
  name: string
}

interface ProductVariation {
  id: number
  name: string
  sku: string
  price: number
  stock: number
  is_default: boolean
  options?: Record<string, string> | null
}

interface ProductAttribute {
  id: number
  name: string
  value: string
}

interface ProductFormSpecification {
  name: string
  value: string
}

interface ProductScale {
  name: string
  values: string[]
}

interface ProductFormScale {
  name: string
  valuesText: string
}

interface ProductFormVariationOption {
  name: string
  value: string
}

interface ProductFormVariation {
  name?: string
  sku?: string
  price?: number
  stock?: number
  is_default?: boolean
  options?: ProductFormVariationOption[]
}

interface Product {
  id: number
  name: string
  slug: string
  short_description?: string | null
  description?: string[] | string | null
  price?: number
  sale_price?: number
  stock_quantity?: number | null
  tax_inclusive?: boolean
  average_rating?: number
  review_count?: number
  status: string
  created_at: string
  qr_code?: string | null
  rejection_reason?: string | null
  images: ProductImageItem[]
  categories: ProductCategory[]
  variations: ProductVariation[]
  attributes?: ProductAttribute[]
  scales?: ProductScale[] | null
}

interface ProductFormValues {
  name: string
  price: number
  short_description?: string
  description?: string
  specifications?: ProductFormSpecification[]
  scales?: ProductFormScale[]
  variations?: ProductFormVariation[]
  stock?: number
  manage_stock?: boolean
  tax_inclusive?: boolean
  categories?: number[]
}

const statusColors: Record<string, string> = {
  pending: 'orange',
  active: 'green',
  rejected: 'red',
}

const MIN_IMAGE_SIZE_BYTES = 1 * 1024
const MAX_IMAGE_SIZE_BYTES = 300 * 1024
const REQUIRED_IMAGE_COUNT = 5

const extractApiErrorMessage = (error: any, fallback: string) => {
  const errors = error?.response?.data?.errors
  if (errors && typeof errors === 'object') {
    const firstError = Object.values(errors).flat().find((value) => typeof value === 'string')
    if (typeof firstError === 'string') {
      return firstError
    }
  }

  return error?.response?.data?.message || fallback
}

const formatCurrency = (value: number) =>
  formatSellerCurrency(Number(value ?? 0), { fromInr: true })

const normalizeDescriptionLines = (description: Product['description']): string[] => {
  if (Array.isArray(description)) {
    return description.filter((line) => typeof line === 'string' && line.trim().length > 0)
  }

  if (typeof description === 'string' && description.trim().length > 0) {
    return [description.trim()]
  }

  return []
}

const normalizeSpecifications = (attributes?: ProductAttribute[]): ProductFormSpecification[] => {
  if (!Array.isArray(attributes) || attributes.length === 0) {
    return []
  }

  return attributes
    .map((attribute) => ({
      name: String(attribute.name ?? '').trim(),
      value: String(attribute.value ?? '').trim(),
    }))
    .filter((attribute) => attribute.name.length > 0 && attribute.value.length > 0)
}

const normalizeScales = (scales?: ProductScale[] | null): ProductFormScale[] => {
  if (!Array.isArray(scales)) {
    return []
  }

  return scales
    .map((scale) => ({
      name: String(scale.name ?? '').trim(),
      valuesText: Array.isArray(scale.values) ? scale.values.join(', ') : '',
    }))
    .filter((scale) => scale.name.length > 0)
}

const normalizeVariationOptions = (options?: Record<string, string> | null): ProductFormVariationOption[] => {
  if (!options || typeof options !== 'object') {
    return []
  }

  return Object.entries(options)
    .map(([name, value]) => ({
      name: String(name).trim(),
      value: String(value ?? '').trim(),
    }))
    .filter((option) => option.name.length > 0 && option.value.length > 0)
}

const normalizeVariationsForForm = (variations?: ProductVariation[]): ProductFormVariation[] => {
  if (!Array.isArray(variations) || variations.length === 0) {
    return [{ name: 'Default', price: 0, stock: 0, is_default: true, options: [] }]
  }

  return variations.map((variation) => ({
    name: variation.name,
    sku: variation.sku,
    price: convertFromInrToSellerCurrency(Number(variation.price ?? 0)),
    stock: Number(variation.stock ?? 0),
    is_default: Boolean(variation.is_default),
    options: normalizeVariationOptions(variation.options),
  }))
}

const formatReviewDate = (value?: string | null) => {
  if (!value) {
    return 'Recently'
  }

  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? 'Recently' : date.toLocaleDateString()
}

const getReviewName = (review: SellerProductReview) =>
  review.user?.displayName || review.user?.name || 'Verified buyer'

const getReviewInitials = (review: SellerProductReview) =>
  review.user?.initials ||
  getReviewName(review)
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('') ||
  'VB'


const ProductsPage = () => {
  const activeCurrency = useSellerCurrencyStore((state) => state.currency)
  const [products, setProducts] = useState<Product[]>([])
  const [categories, setCategories] = useState<ProductCategory[]>([])
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [searchText, setSearchText] = useState('')
  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs] | null>([
    dayjs().subtract(6, 'day'),
    dayjs(),
  ])
  const [modalVisible, setModalVisible] = useState(false)
  const [editingProduct, setEditingProduct] = useState<Product | null>(null)
  const [previewProduct, setPreviewProduct] = useState<Product | null>(null)
  const [previewVisible, setPreviewVisible] = useState(false)
  const [previewLoading, setPreviewLoading] = useState(false)
  const [reviewProduct, setReviewProduct] = useState<Product | null>(null)
  const [reviewModalVisible, setReviewModalVisible] = useState(false)
  const [reviewsLoading, setReviewsLoading] = useState(false)
  const [productReviews, setProductReviews] = useState<SellerProductReview[]>([])
  const [fileList, setFileList] = useState<UploadFile[]>([])
  const [form] = Form.useForm<ProductFormValues>()
  const { hasPermission } = useSellerAuth()
  const { message } = App.useApp()

  const canCreateProduct = hasPermission('products.create')
  const canUpdateProduct = hasPermission('products.update')
  const canDeleteProduct = hasPermission('products.delete')

  const fetchCategories = useCallback(async () => {
    try {
      const res = await sellerProductsAPI.categories()
      setCategories(res.data.data ?? [])
    } catch {
      message.error('Failed to load categories')
    }
  }, [message])

  const fetchProducts = useCallback(async (query = '', nextDateRange: [Dayjs, Dayjs] | null = dateRange) => {
    setLoading(true)
    try {
      const params: { search?: string; start_date?: string; end_date?: string } = {}

      if (query.trim()) {
        params.search = query.trim()
      }

      if (nextDateRange) {
        params.start_date = nextDateRange[0].format('YYYY-MM-DD')
        params.end_date = nextDateRange[1].format('YYYY-MM-DD')
      }

      const response = await sellerProductsAPI.list(Object.keys(params).length > 0 ? params : undefined)
      setProducts(response.data.data.data ?? [])
    } catch (error: any) {
      message.error(extractApiErrorMessage(error, 'Failed to load products'))
    } finally {
      setLoading(false)
    }
  }, [dateRange, message])

  useEffect(() => {
    void fetchProducts()
    void fetchCategories()
  }, [fetchCategories, fetchProducts])

  const filteredProducts = useMemo(() => {
    const query = searchText.trim().toLowerCase()
    if (!query) {
      return products
    }

    return products.filter((product) => {
      const haystacks = [
        product.name,
        product.slug,
        ...(product.categories?.map((category) => category.name) ?? []),
      ]

      return haystacks.some((value) => value?.toLowerCase().includes(query))
    })
  }, [products, searchText])

  const openCreateModal = () => {
    if (!canCreateProduct) {
      message.warning('You are not permitted to create products.')
      return
    }

    setEditingProduct(null)
    setFileList([])
    form.resetFields()
    form.setFieldsValue({
      manage_stock: true,
      tax_inclusive: false,
      stock: 0,
      specifications: [],
      scales: [],
      variations: [{ name: 'Default', price: 0, stock: 0, is_default: true, options: [] }],
    })
    setModalVisible(true)
  }

  const openEditModal = (product: Product) => {
    if (!canUpdateProduct) {
      message.warning('You are not permitted to update products.')
      return
    }

    setEditingProduct(product)
    setFileList([])
    form.setFieldsValue({
      name: product.name,
      price: convertFromInrToSellerCurrency(Number(product.price ?? product.sale_price ?? product.variations?.[0]?.price ?? 0)),
      short_description: product.short_description ?? '',
      description: normalizeDescriptionLines(product.description).join('\n'),
      specifications: normalizeSpecifications(product.attributes),
      scales: normalizeScales(product.scales),
      variations: normalizeVariationsForForm(product.variations),
      stock: product.stock_quantity ?? product.variations?.[0]?.stock ?? 0,
      manage_stock: true,
      tax_inclusive: Boolean(product.tax_inclusive),
      categories: product.categories?.map((category) => category.id),
    })
    setModalVisible(true)
  }

  const closeModal = () => {
    setModalVisible(false)
    setEditingProduct(null)
    setFileList([])
    form.resetFields()
  }

  const openPreviewModal = async (product: Product) => {
    setPreviewProduct(product)
    setPreviewVisible(true)
    setPreviewLoading(true)

    try {
      const response = await sellerProductsAPI.get(product.id)
      const detail = (response.data?.data ?? response.data) as Partial<Product> | undefined

      if (detail) {
        setPreviewProduct({
          ...product,
          ...detail,
          images: detail.images ?? product.images ?? [],
          categories: detail.categories ?? product.categories ?? [],
          variations: detail.variations ?? product.variations ?? [],
          attributes: detail.attributes ?? product.attributes ?? [],
        })
      }
    } catch (error: any) {
      message.error(extractApiErrorMessage(error, 'Failed to load product preview'))
    } finally {
      setPreviewLoading(false)
    }
  }

  const closePreviewModal = () => {
    setPreviewVisible(false)
    setPreviewProduct(null)
    setPreviewLoading(false)
  }

  const openReviewsModal = async (product: Product) => {
    setReviewProduct(product)
    setProductReviews([])
    setReviewModalVisible(true)
    setReviewsLoading(true)

    try {
      const response = await sellerProductsAPI.reviews(product.id)
      const payload = response.data?.data
      setReviewProduct((current) => ({
        ...(current ?? product),
        average_rating: payload?.product.average_rating ?? product.average_rating ?? 0,
        review_count: payload?.product.review_count ?? product.review_count ?? 0,
      }))
      setProductReviews(payload?.reviews ?? [])
    } catch (error: any) {
      message.error(extractApiErrorMessage(error, 'Failed to load product reviews'))
    } finally {
      setReviewsLoading(false)
    }
  }

  const closeReviewsModal = () => {
    setReviewModalVisible(false)
    setReviewProduct(null)
    setProductReviews([])
    setReviewsLoading(false)
  }

  const validateImageFile = (file: UploadFile) => {
    const size = file.size ?? 0

    if (size < MIN_IMAGE_SIZE_BYTES || size > MAX_IMAGE_SIZE_BYTES) {
      message.error('Each product image must be between 150 KB and 300 KB.')
      return Upload.LIST_IGNORE
    }

    return false
  }

  const handleDelete = async (id: number) => {
    if (!canDeleteProduct) {
      message.warning('You are not permitted to delete products.')
      return
    }

    try {
      await sellerProductsAPI.delete(id)
      message.success('Product deleted')
      await fetchProducts(searchText)
    } catch (error: any) {
      message.error(extractApiErrorMessage(error, 'Delete failed'))
    }
  }

  const handleSubmit = async (values: ProductFormValues) => {
    if (editingProduct && !canUpdateProduct) {
      message.warning('You are not permitted to update products.')
      return
    }

    if (!editingProduct && !canCreateProduct) {
      message.warning('You are not permitted to create products.')
      return
    }

    if (!editingProduct && fileList.length !== REQUIRED_IMAGE_COUNT) {
      message.error('Upload exactly 5 product images before creating a product.')
      return
    }

    if (editingProduct && fileList.length > 0 && fileList.length !== REQUIRED_IMAGE_COUNT) {
      message.error('When replacing product images, upload exactly 5 images.')
      return
    }

    if (!editingProduct && !values.tax_inclusive) {
      message.error('Please enable "Price includes tax" before creating a product.')
      return
    }

    const formData = new FormData()
    formData.append('name', values.name)
    formData.append('price', String(convertSellerCurrencyToInr(values.price)))
    formData.append('short_description', values.short_description ?? '')
    formData.append('description', values.description ?? '')
    const normalizedSpecifications = (values.specifications ?? [])
      .map((specification) => ({
        name: (specification?.name ?? '').trim(),
        value: (specification?.value ?? '').trim(),
      }))
      .filter((specification) => specification.name.length > 0 && specification.value.length > 0)
    formData.append('attributes', JSON.stringify(normalizedSpecifications))
    const normalizedScales = (values.scales ?? [])
      .map((scale) => ({
        name: (scale?.name ?? '').trim(),
        values: String(scale?.valuesText ?? '')
          .split(',')
          .map((value) => value.trim())
          .filter(Boolean),
      }))
      .filter((scale) => scale.name.length > 0 && scale.values.length > 0)
    formData.append('scales', JSON.stringify(normalizedScales))
    const formVariations = values.variations ?? []
    const defaultVariationIndex = Math.max(0, formVariations.findIndex((variation) => variation.is_default))
    const normalizedVariations = formVariations
      .map((variation, index) => {
        const options = (variation.options ?? [])
          .map((option) => ({
            name: (option?.name ?? '').trim(),
            value: (option?.value ?? '').trim(),
          }))
          .filter((option) => option.name.length > 0 && option.value.length > 0)
          .reduce<Record<string, string>>((acc, option) => {
            acc[option.name] = option.value
            return acc
          }, {})

        return {
          name: (variation.name ?? '').trim() || (Object.values(options).join(' / ') || `Variant ${index + 1}`),
          sku: (variation.sku ?? '').trim(),
          price: convertSellerCurrencyToInr(Number(variation.price ?? values.price ?? 0)),
          stock: Number(variation.stock ?? 0),
          is_default: index === defaultVariationIndex,
          options,
        }
      })
      .filter((variation) => Number.isFinite(variation.price) && variation.price >= 0)
    formData.append('variations', JSON.stringify(normalizedVariations))
    formData.append('manage_stock', String(values.manage_stock ?? true))
    formData.append('tax_inclusive', String(values.tax_inclusive ?? false))
    formData.append('stock', String(values.stock ?? 0))

    values.categories?.forEach((catId) => {
      formData.append('category_ids[]', String(catId))
    })

    fileList.forEach((file) => {
      if (file.originFileObj) {
        formData.append('images[]', file.originFileObj)
      }
    })

    setSaving(true)
    try {
      if (editingProduct) {
        await sellerProductsAPI.update(editingProduct.id, formData)
        message.success('Product updated')
      } else {
        await sellerProductsAPI.create(formData)
        message.success('Product created and submitted for approval')
      }
      closeModal()
      await fetchProducts(searchText)
    } catch (error: any) {
      message.error(extractApiErrorMessage(error, 'Operation failed'))
    } finally {
      setSaving(false)
    }
  }

  const columns: ColumnsType<Product> = [
    {
      title: 'Image',
      key: 'image',
      render: (_, record) => {
        const imageUrl = record.images?.find((image) => image.is_primary)?.url ?? record.images?.[0]?.url
        return imageUrl ? <Image src={imageUrl} alt={record.name} width={52} height={52} style={{ objectFit: 'cover', borderRadius: 8 }} /> : <div style={{ width: 52, height: 52, background: '#f5f5f5', borderRadius: 8 }} />
      },
    },
    {
      title: 'Product',
      key: 'product',
      render: (_, record) => (
        <div>
          <button
            type="button"
            onClick={() => void openPreviewModal(record)}
            style={{
              background: 'none',
              border: 0,
              padding: 0,
              color: '#0f766e',
              fontWeight: 700,
              cursor: 'pointer',
              textAlign: 'left',
            }}
          >
            {record.name}
          </button>
          <div style={{ color: '#64748b', fontSize: 12 }}>{record.slug}</div>
        </div>
      ),
    },
    {
      title: 'Categories',
      key: 'categories',
      render: (_, record) => (record.categories?.length ? record.categories.map((category) => <Tag key={category.id}>{category.name}</Tag>) : <span style={{ color: '#94a3b8' }}>None</span>),
    },
    {
      title: 'Price',
      key: 'price',
      render: (_, record) => formatCurrency(Number(record.price ?? record.sale_price ?? 0)),
    },
    {
      title: 'Stock',
      key: 'stock',
      render: (_, record) => record.stock_quantity ?? record.variations?.reduce((total, variation) => total + Number(variation.stock ?? 0), 0) ?? 0,
    },
    {
      title: 'Reviews',
      key: 'reviews',
      render: (_, record) => (
        <Space size={6} wrap>
          <Rate disabled allowHalf value={Number(record.average_rating ?? 0)} style={{ fontSize: 13 }} />
          <Typography.Text type="secondary">{record.review_count ?? 0}</Typography.Text>
        </Space>
      ),
    },
    {
      title: 'Status',
      key: 'status',
      render: (_, record) => <Tag color={statusColors[record.status] ?? 'default'}>{record.status}</Tag>,
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button size="small" icon={<EyeOutlined />} onClick={() => void openPreviewModal(record)}>
            Preview
          </Button>
          <Button size="small" onClick={() => void openReviewsModal(record)}>
            Reviews
          </Button>
          {canUpdateProduct ? (
            <Button size="small" icon={<EditOutlined />} onClick={() => openEditModal(record)}>
              Edit
            </Button>
          ) : null}
          {canDeleteProduct ? (
            <Button size="small" danger icon={<DeleteOutlined />} onClick={() => void handleDelete(record.id)}>
              Delete
            </Button>
          ) : null}
          {!canUpdateProduct && !canDeleteProduct ? <Tag color="default">Read only</Tag> : null}
        </Space>
      ),
    },
  ]

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, marginBottom: 24, flexWrap: 'wrap' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 28 }}>Products Management</h1>
          <div style={{ color: '#64748b', marginTop: 6 }}>Create, update, and track the approval state of your seller catalog.</div>
        </div>
        <Space wrap>
          <Input
            value={searchText}
            onChange={(event) => setSearchText(event.target.value)}
            onPressEnter={() => void fetchProducts(searchText)}
            placeholder="Search products"
            prefix={<SearchOutlined />}
            style={{ width: 260 }}
          />
          <DatePicker.RangePicker
            value={dateRange}
            format="YYYY-MM-DD"
            allowClear
            onChange={(dates) => {
              if (!dates) {
                setDateRange(null)
                void fetchProducts(searchText, null)
                return
              }

              if (dates[0] && dates[1]) {
                const nextRange: [Dayjs, Dayjs] = [dates[0], dates[1]]
                setDateRange(nextRange)
                void fetchProducts(searchText, nextRange)
              }
            }}
          />
          <Button onClick={() => void fetchProducts(searchText)}>Search</Button>
          {canCreateProduct ? (
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreateModal}>
              Add Product
            </Button>
          ) : null}
        </Space>
      </div>

      <Table columns={columns} dataSource={filteredProducts} rowKey="id" loading={loading} pagination={{ pageSize: 10 }} scroll={{ x: 1080 }} />

      <Modal title={editingProduct ? 'Edit Product' : 'Create Product'} open={modalVisible} onCancel={closeModal} footer={null} width={720}>
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{
            manage_stock: true,
            stock: 0,
            specifications: [],
            scales: [],
            variations: [{ name: 'Default', price: 0, stock: 0, is_default: true, options: [] }],
          }}
        >
          <Form.Item name="name" label="Product Name" rules={[{ required: true, message: 'Product name is required' }]}>
            <Input placeholder="Product name" />
          </Form.Item>
          <Form.Item name="categories" label="Categories" rules={[{ required: true, message: 'Select at least one category' }]}>
            <Select
              mode="multiple"
              placeholder="Select categories"
              options={categories.map((category) => ({
                label: category.name,
                value: category.id,
              }))}
            />
          </Form.Item>
          <Space style={{ display: 'flex' }} align="start">
            <Form.Item name="price" label={`Price (${activeCurrency})`} rules={[{ required: true, message: 'Price is required' }]} style={{ width: '100%' }}>
              <InputNumber min={0} step={0.01} precision={2} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item name="stock" label="Stock" style={{ width: '100%' }}>
              <InputNumber min={0} style={{ width: '100%' }} />
            </Form.Item>
          </Space>
          <Form.Item name="manage_stock" valuePropName="checked">
            <Checkbox>Manage stock for this product</Checkbox>
          </Form.Item>
          <Form.Item
            name="tax_inclusive"
            valuePropName="checked"
            rules={[
              {
                validator: (_, value) => {
                  if (editingProduct || value === true) {
                    return Promise.resolve()
                  }

                  return Promise.reject(new Error('Please check "Price includes tax" to create the product.'))
                },
              },
            ]}
          >
            <Checkbox>Price includes tax</Checkbox>
          </Form.Item>
          <Form.Item name="short_description" label="Short Description">
            <Input placeholder="Short summary for the catalog" />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input.TextArea rows={5} placeholder="Full product description" />
          </Form.Item>

          <Card size="small" title="Product Specifications" style={{ marginBottom: 16 }}>
            <Typography.Text type="secondary">
              Add key details that customers should see under product specifications.
            </Typography.Text>
            <Form.List name="specifications">
              {(fields, { add, remove }) => (
                <Space orientation="vertical" size={10} style={{ width: '100%', marginTop: 12 }}>
                  {fields.map((field) => {
                    const { key: fieldKey, ...restField } = field

                    return (
                    <Space key={fieldKey} align="start" style={{ display: 'flex' }}>
                      <Form.Item
                        {...restField}
                        name={[field.name, 'name']}
                        rules={[{ required: true, message: 'Enter a specification name' }]}
                        style={{ flex: 1, marginBottom: 0 }}
                      >
                        <Input placeholder="Specification name (e.g. Lens Type)" />
                      </Form.Item>
                      <Form.Item
                        {...restField}
                        name={[field.name, 'value']}
                        rules={[{ required: true, message: 'Enter the specification value' }]}
                        style={{ flex: 1.2, marginBottom: 0 }}
                      >
                        <Input placeholder="Specification value (e.g. UV400)" />
                      </Form.Item>
                      <Button
                        type="text"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => remove(field.name)}
                        aria-label="Remove specification"
                      />
                    </Space>
                  )})}

                  <Button
                    type="dashed"
                    icon={<PlusOutlined />}
                    onClick={() => add({ name: '', value: '' })}
                    block
                  >
                    Add specification
                  </Button>
                </Space>
              )}
            </Form.List>
          </Card>

          <Card size="small" title="Product Scales" style={{ marginBottom: 16 }}>
            <Typography.Text type="secondary">
              Define the option axes for this product, such as Size and Color for shoes, Gender and Color for watches, or RAM/Storage and Color for phones.
            </Typography.Text>
            <Form.List name="scales">
              {(fields, { add, remove }) => (
                <Space orientation="vertical" size={10} style={{ width: '100%', marginTop: 12 }}>
                  {fields.map((field) => {
                    const { key: fieldKey, ...restField } = field

                    return (
                      <Space key={fieldKey} align="start" style={{ display: 'flex' }}>
                        <Form.Item
                          {...restField}
                          name={[field.name, 'name']}
                          style={{ flex: 1, marginBottom: 0 }}
                        >
                          <Input placeholder="Scale name (e.g. Size)" />
                        </Form.Item>
                        <Form.Item
                          {...restField}
                          name={[field.name, 'valuesText']}
                          style={{ flex: 1.4, marginBottom: 0 }}
                        >
                          <Input placeholder="Values, comma separated (e.g. 7, 8, 9)" />
                        </Form.Item>
                        <Button
                          type="text"
                          danger
                          icon={<DeleteOutlined />}
                          onClick={() => remove(field.name)}
                          aria-label="Remove scale"
                        />
                      </Space>
                    )
                  })}

                  <Button
                    type="dashed"
                    icon={<PlusOutlined />}
                    onClick={() => add({ name: '', valuesText: '' })}
                    block
                  >
                    Add scale
                  </Button>
                </Space>
              )}
            </Form.List>
          </Card>

          <Card size="small" title="Product Variants" style={{ marginBottom: 16 }}>
            <Typography.Text type="secondary">
              Add sellable combinations. Leaving this as one Default variant keeps the product simple.
            </Typography.Text>
            <Form.List name="variations">
              {(fields, { add, remove }) => (
                <Space orientation="vertical" size={12} style={{ width: '100%', marginTop: 12 }}>
                  {fields.map((field, index) => (
                    <Card
                      key={field.key}
                      size="small"
                      title={`Variant ${index + 1}`}
                      extra={fields.length > 1 ? (
                        <Button type="text" danger icon={<DeleteOutlined />} onClick={() => remove(field.name)} />
                      ) : null}
                    >
                      <Space style={{ display: 'flex' }} align="start" wrap>
                        <Form.Item name={[field.name, 'name']} label="Name" style={{ minWidth: 180, flex: 1 }}>
                          <Input placeholder="Black / 8GB + 128GB" />
                        </Form.Item>
                        <Form.Item name={[field.name, 'sku']} label="SKU" style={{ minWidth: 160, flex: 1 }}>
                          <Input placeholder="Optional SKU" />
                        </Form.Item>
                        <Form.Item name={[field.name, 'price']} label={`Price (${activeCurrency})`} rules={[{ required: true, message: 'Price is required' }]} style={{ minWidth: 140, flex: 1 }}>
                          <InputNumber min={0} step={0.01} precision={2} style={{ width: '100%' }} />
                        </Form.Item>
                        <Form.Item name={[field.name, 'stock']} label="Stock" style={{ minWidth: 120, flex: 1 }}>
                          <InputNumber min={0} style={{ width: '100%' }} />
                        </Form.Item>
                        <Form.Item name={[field.name, 'is_default']} valuePropName="checked" label="Default">
                          <Checkbox />
                        </Form.Item>
                      </Space>
                      <Form.List name={[field.name, 'options']}>
                        {(optionFields, optionActions) => (
                          <Space orientation="vertical" size={8} style={{ width: '100%' }}>
                            {optionFields.map((optionField) => (
                              <Space key={optionField.key} align="start" style={{ display: 'flex' }}>
                                <Form.Item name={[optionField.name, 'name']} style={{ flex: 1, marginBottom: 0 }}>
                                  <Input placeholder="Option name (e.g. Color)" />
                                </Form.Item>
                                <Form.Item name={[optionField.name, 'value']} style={{ flex: 1, marginBottom: 0 }}>
                                  <Input placeholder="Option value (e.g. Black)" />
                                </Form.Item>
                                <Button
                                  type="text"
                                  danger
                                  icon={<DeleteOutlined />}
                                  onClick={() => optionActions.remove(optionField.name)}
                                />
                              </Space>
                            ))}
                            <Button type="dashed" size="small" onClick={() => optionActions.add({ name: '', value: '' })}>
                              Add variant option
                            </Button>
                          </Space>
                        )}
                      </Form.List>
                    </Card>
                  ))}

                  <Button
                    type="dashed"
                    icon={<PlusOutlined />}
                    onClick={() => add({ name: '', price: 0, stock: 0, is_default: false, options: [] })}
                    block
                  >
                    Add variant
                  </Button>
                </Space>
              )}
            </Form.List>
          </Card>

          <Form.Item label="Images" extra="Upload exactly 5 product images. Each image must be 300 KB or smaller.">
            <Upload
              listType="picture"
              multiple
              maxCount={REQUIRED_IMAGE_COUNT}
              beforeUpload={validateImageFile}
              fileList={fileList}
              onChange={({ fileList: nextFileList }) => setFileList(nextFileList)}
            >
              <Button icon={<UploadOutlined />}>Select Images</Button>
            </Upload>
          </Form.Item>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
            <Button onClick={closeModal}>Cancel</Button>
            <Button type="primary" htmlType="submit" loading={saving}>
              {editingProduct ? 'Update Product' : 'Create Product'}
            </Button>
          </div>
        </Form>
      </Modal>

      <Modal
        title="Product Preview"
        open={previewVisible}
        onCancel={closePreviewModal}
        width={900}
        destroyOnHidden
        footer={[
          <Button key="close" onClick={closePreviewModal}>
            Close
          </Button>,
        ]}
      >
        {previewLoading ? (
          <div style={{ minHeight: 220, display: 'grid', placeItems: 'center' }}>
            <Spin size="large" />
          </div>
        ) : previewProduct ? (
          <Space orientation="vertical" size="large" style={{ width: '100%' }}>
            <Card>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
                <div>
                  <Typography.Title level={4} style={{ margin: 0 }}>
                    {previewProduct.name}
                  </Typography.Title>
                  <Typography.Text type="secondary">{previewProduct.slug}</Typography.Text>
                </div>
                <Tag color={statusColors[previewProduct.status] ?? 'default'}>{previewProduct.status}</Tag>
              </div>
            </Card>

            <Card title="Product Images">
              {previewProduct.images?.length ? (
                <Image.PreviewGroup>
                  <div
                    className="seller-card-grid"
                    style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))' }}
                  >
                    {previewProduct.images.map((image, index) => (
                      <Image
                        key={`${image.id}-${index}`}
                        src={image.url}
                        alt={`${previewProduct.name} ${index + 1}`}
                        style={{ height: 130, objectFit: 'cover', borderRadius: 8 }}
                      />
                    ))}
                  </div>
                </Image.PreviewGroup>
              ) : (
                <Typography.Text type="secondary">No product images available.</Typography.Text>
              )}
            </Card>

            <div className="seller-card-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))' }}>
              <Card size="small">
                <Typography.Text type="secondary">Price</Typography.Text>
                <div style={{ fontWeight: 700, marginTop: 6 }}>
                  {formatCurrency(Number(previewProduct.price ?? previewProduct.sale_price ?? 0))}
                </div>
              </Card>
              <Card size="small">
                <Typography.Text type="secondary">Stock</Typography.Text>
                <div style={{ fontWeight: 700, marginTop: 6 }}>
                  {previewProduct.stock_quantity ??
                    previewProduct.variations?.reduce((total, variation) => total + Number(variation.stock ?? 0), 0) ??
                    0}
                </div>
              </Card>
              <Card size="small">
                <Typography.Text type="secondary">Created</Typography.Text>
                <div style={{ fontWeight: 700, marginTop: 6 }}>
                  {new Date(previewProduct.created_at).toLocaleString()}
                </div>
              </Card>
            </div>

            <Card title="Categories">
              {previewProduct.categories?.length ? (
                <Space wrap>
                  {previewProduct.categories.map((category) => (
                    <Tag key={category.id}>{category.name}</Tag>
                  ))}
                </Space>
              ) : (
                <Typography.Text type="secondary">No categories assigned.</Typography.Text>
              )}
            </Card>

            {previewProduct.scales?.length ? (
              <Card title="Scales">
                <Space wrap>
                  {previewProduct.scales.map((scale) => (
                    <Tag key={scale.name}>
                      {scale.name}: {scale.values.join(', ')}
                    </Tag>
                  ))}
                </Space>
              </Card>
            ) : null}

            {previewProduct.variations?.length ? (
              <Card title="Variants">
                <Table
                  size="small"
                  pagination={false}
                  rowKey={(variation) => variation.id || variation.sku}
                  dataSource={previewProduct.variations}
                  columns={[
                    {
                      title: 'Name',
                      key: 'name',
                      render: (_, variation) => (
                        <Space direction="vertical" size={2}>
                          <Typography.Text strong>{variation.name || 'Default'}</Typography.Text>
                          <Typography.Text type="secondary">{variation.sku || 'Auto SKU'}</Typography.Text>
                        </Space>
                      ),
                    },
                    {
                      title: 'Options',
                      key: 'options',
                      render: (_, variation) => variation.options && Object.keys(variation.options).length > 0 ? (
                        <Space wrap>
                          {Object.entries(variation.options).map(([name, value]) => (
                            <Tag key={`${variation.id}-${name}`}>{name}: {value}</Tag>
                          ))}
                        </Space>
                      ) : <Typography.Text type="secondary">None</Typography.Text>,
                    },
                    {
                      title: 'Price',
                      key: 'price',
                      render: (_, variation) => formatCurrency(Number(variation.price ?? 0)),
                    },
                    {
                      title: 'Stock',
                      dataIndex: 'stock',
                      key: 'stock',
                    },
                    {
                      title: 'Default',
                      key: 'is_default',
                      render: (_, variation) => variation.is_default ? <Tag color="green">Default</Tag> : null,
                    },
                  ]}
                />
              </Card>
            ) : null}

            <Card title="Details">
              {previewProduct.short_description ? (
                <Typography.Paragraph style={{ marginBottom: 10 }}>
                  {previewProduct.short_description}
                </Typography.Paragraph>
              ) : null}

              {normalizeDescriptionLines(previewProduct.description).length ? (
                <ul style={{ margin: 0, paddingLeft: 18 }}>
                  {normalizeDescriptionLines(previewProduct.description).map((line, index) => (
                    <li key={`${line}-${index}`} style={{ marginBottom: 4 }}>
                      {line}
                    </li>
                  ))}
                </ul>
              ) : (
                <Typography.Text type="secondary">No additional description provided.</Typography.Text>
              )}
            </Card>

            {previewProduct.attributes?.length ? (
              <Card title="Attributes">
                <Space wrap>
                  {previewProduct.attributes.map((attribute) => (
                    <Tag key={attribute.id}>{attribute.name}: {attribute.value}</Tag>
                  ))}
                </Space>
              </Card>
            ) : null}
          </Space>
        ) : (
          <Typography.Text type="secondary">No product selected.</Typography.Text>
        )}
      </Modal>

      <Modal
        title={reviewProduct ? `Reviews for ${reviewProduct.name}` : 'Product Reviews'}
        open={reviewModalVisible}
        onCancel={closeReviewsModal}
        width={760}
        destroyOnHidden
        footer={[
          <Button key="close" onClick={closeReviewsModal}>
            Close
          </Button>,
        ]}
      >
        {reviewsLoading ? (
          <div style={{ minHeight: 220, display: 'grid', placeItems: 'center' }}>
            <Spin size="large" />
          </div>
        ) : reviewProduct ? (
          <Space orientation="vertical" size="middle" style={{ width: '100%' }}>
            <Card size="small">
              <Space align="center" wrap>
                <Rate disabled allowHalf value={Number(reviewProduct.average_rating ?? 0)} />
                <Typography.Text strong>{Number(reviewProduct.average_rating ?? 0).toFixed(1)}</Typography.Text>
                <Typography.Text type="secondary">
                  average from {reviewProduct.review_count ?? productReviews.length} reviews
                </Typography.Text>
              </Space>
            </Card>

            {productReviews.length > 0 ? (
              <List
                itemLayout="vertical"
                dataSource={productReviews}
                renderItem={(review) => (
                  <List.Item key={review.id}>
                    <List.Item.Meta
                      avatar={
                        <Avatar src={review.user?.avatarUrl || undefined}>
                          {getReviewInitials(review)}
                        </Avatar>
                      }
                      title={
                        <Space wrap>
                          <Typography.Text strong>{getReviewName(review)}</Typography.Text>
                          <Tag color="green">
                            {review.user?.verifiedBuyer === false ? 'Customer review' : 'Verified buyer'}
                          </Tag>
                        </Space>
                      }
                      description={formatReviewDate(review.createdAt)}
                    />
                    <Space direction="vertical" size={8} style={{ width: '100%' }}>
                      <Rate disabled allowHalf value={review.rating} style={{ fontSize: 16 }} />
                      <Typography.Paragraph style={{ marginBottom: 0, whiteSpace: 'pre-wrap' }}>
                        {review.comment?.trim() || 'No written comment was added.'}
                      </Typography.Paragraph>
                    </Space>
                  </List.Item>
                )}
              />
            ) : (
              <Empty description="No reviews have been submitted for this product yet." />
            )}
          </Space>
        ) : (
          <Empty description="No product selected." />
        )}
      </Modal>
    </div>
  )
}

export default ProductsPage
